import React from 'react';

import { DashboardListPage, DashboardViewPage } from '@sms/dashboard-dashboard-ui';
import { DecisionTableCreatePage, DecisionTableEditPage, DecisionTableListPage } from '@sms/decision-table-decision-ui';
import {
  BuildNewProcessPage,
  DeployProcessPage,
  DocumentationProcessPage,
  EditProcessPage,
  InstanceProcessPage,
  MonitorProcessPage,
  ProcessesPage,
  StartProcessPage,
} from '@sms/fluere-process-fluere-process-ui';
import { CreateRulePage, EditRulePage, RuleListPage } from '@sms/journal-cep-journal-cep-ui';
import {
  CreateEventPage,
  CreateEventTemplatePageWithProvider,
  EditEventPage,
  EditEventTemplatePageWithProvider,
  EventListPage,
  EventTemplateListPage,
  EventViewPage,
} from '@sms/journal-event-journal-event-ui';
import {
  TaskPage,
  TaskListPage,
  CreateTaskPage,
  CategoriesListPage as TaskCategoriesListPage,
  CreateTemplatePage as CreateTaskTemplatePage,
  EditTemplatePage as EditTaskTemplatePage,
  TemplatesListPage as TaskTemplatesListPage,
} from '@sms/journal-task-journal-task-ui';
import { PageConfig, UnderConstructionPage } from '@sms/plasma-ui';
import { CreateReportPage, CreateTemplatePage, EditTemplatePage, MainPage, TemplatesPage } from '@sms/report-report-ui';
import { CreateFormPage, EditFormPage, FormsListPage } from '@sms/fluere-form-fluere-form-ui';

import app from '../config/app.json';
import { PageWithMenu } from './PageWithMenu';
import RootPage from './RootPage';
import { WebgeePage } from './Webgee';
import { VeroPage } from './Vero';
import { ManualInputPage } from '@sms/aegea-viridis-solution-aegea-viridis-solution-ui';
import { AuditTrailsPage, AuditSettingsPage } from './Audit';
import { DashboardAdmin } from './DashboardAdmin';

const withMenu = (PageComponent: React.ComponentType<any>) => {
  return (props: any) => (
    <PageWithMenu>
      <PageComponent {...props} />
    </PageWithMenu>
  );
};

const pages: PageConfig[] = [
  {
    key: 'Root',
    path: '/',
    page: withMenu(RootPage),
  },
  {
    key: 'RootAppCode',
    path: `/${app.code}`,
    page: withMenu(RootPage),
  },
  {
    key: 'Processes',
    path: `/${app.code}/processes`,
    page: withMenu(ProcessesPage),
    title: 'app.menu.processes',
    permissions: ['process.view'],
  },
  {
    key: 'NewProcesses',
    path: `/${app.code}/processes/new`,
    page: withMenu(BuildNewProcessPage),
    title: 'app.menu.newProcess',
    permissions: ['process.definition.edit'],
  },
  {
    key: 'DeployProcesses',
    path: `/${app.code}/processes/deploy/:modelId`,
    page: withMenu(DeployProcessPage),
    title: 'app.menu.deployProcess',
    permissions: ['process.definition.edit'],
  },
  {
    key: 'StartProcess',
    path: `/${app.code}/processes/start/:id`,
    page: withMenu(StartProcessPage),
    title: 'app.menu.startProcess',
    permissions: ['process.edit'],
  },
  {
    key: 'DocumentationProcessPage',
    path: `/${app.code}/processes/documentation/:id`,
    page: withMenu(DocumentationProcessPage),
    title: 'app.menu.documentationProcess',
    permissions: ['process.view'],
  },
  {
    key: 'MonitorProcessPage',
    path: `/${app.code}/processes/monitor/:id`,
    page: withMenu(MonitorProcessPage),
    title: 'app.menu.monitorProcess',
    permissions: ['process.view'],
  },
  {
    key: 'InstanceProcessPage',
    path: `/${app.code}/processes/monitor/:id/instance/:instanceId`,
    page: withMenu(InstanceProcessPage),
    title: 'app.menu.instanceProcess',
    permissions: ['process.view'],
  },
  {
    key: 'EditProcessPage',
    path: `/${app.code}/processes/edit/:id`,
    page: withMenu(EditProcessPage),
    title: 'app.menu.editProcess',
    permissions: ['process.definition.edit'],
  },
  {
    key: 'Forms',
    path: `/${app.code}/forms`,
    page: withMenu(FormsListPage),
    title: 'app.menu.forms',
    permissions: ['forms.view'],
  },
  {
    key: "CreateForms",
    path: `/${app.code}/forms/create`,
    page: withMenu(CreateFormPage),
    title: "app.menu.tasks",
  },
  {
    key: "EditForms",
    path: `/${app.code}/forms/edit/:id/:versionNumber`,
    page: withMenu(EditFormPage),
    title: "app.menu.tasks",
  },
  {
    key: 'TaskList',
    path: `/${app.code}/tasks`,
    page: withMenu(TaskListPage),
    title: 'app.menu.tasks',
    permissions: ['task.view'],
  },
  {
    key: 'TaskTemplatesList',
    path: `/${app.code}/tasks/settings/templates`,
    page: withMenu(TaskTemplatesListPage),
    permissions: ['task.template.view'],
  },
  {
    key: 'CreateTaskTemplate',
    path: `/${app.code}/tasks/settings/templates/new`,
    page: withMenu(CreateTaskTemplatePage),
    permissions: ['task.template.edit'],
  },
  {
    key: 'EditTaskTemplate',
    path: `/${app.code}/tasks/settings/templates/edit/:id`,
    page: withMenu(EditTaskTemplatePage),
    permissions: [['task.template.view']],
  },
  {
    key: 'TaskCategoriesList',
    path: `/${app.code}/tasks/settings/categories`,
    page: withMenu(TaskCategoriesListPage),
    permissions: ['task.category.view'],
  },
  {
    key: 'Task',
    path: `/${app.code}/tasks/execute/:taskId`,
    page: withMenu(TaskPage),
    permissions: ['task.view'],
  },
  {
    key: 'CreateTask',
    path: `/${app.code}/tasks/create`,
    page: withMenu(CreateTaskPage),
    permissions: ['task.edit'],
  },
  {
    key: 'Report',
    path: `/${app.code}/reports`,
    page: withMenu(MainPage),
    title: 'app.menu.reports',
    permissions: ['report.view'],
  },
  {
    key: 'NewReport',
    path: `/${app.code}/reports/new`,
    page: withMenu(CreateReportPage),
    title: 'app.menu.reports',
    permissions: ['report.edit'],
  },
  {
    key: 'ReportTemplate',
    path: `/${app.code}/reports/settings/template`,
    page: withMenu(TemplatesPage),
    title: 'app.menu.reports',
    permissions: ['report.template.view'],
  },
  {
    key: 'NewReportTemplate',
    path: `/${app.code}/reports/settings/template/new`,
    page: withMenu(CreateTemplatePage),
    title: 'app.menu.reports',
    permissions: ['report.template.edit'],
  },
  {
    key: 'EditReportTemplate',
    path: `/${app.code}/reports/settings/template/edit/:id`,
    page: withMenu(EditTemplatePage),
    title: 'app.menu.reports',
    permissions: ['report.template.view'],
  },
  {
    key: 'EventList',
    path: `/${app.code}/events`,
    page: withMenu(EventListPage),
    permissions: ['event.view'],
  },
  {
    key: 'EventCategories',
    path: `/${app.code}/events/settings/categories`,
    page: withMenu(UnderConstructionPage),
    permissions: ['event.category.view'],
  },
  {
    key: 'EventTemplates',
    path: `/${app.code}/events/settings/templates`,
    page: withMenu(EventTemplateListPage),
    permissions: ['event.template.view'],
  },
  {
    key: 'CreateEventTemplates',
    path: `/${app.code}/events/settings/templates/create`,
    page: withMenu(CreateEventTemplatePageWithProvider),
    permissions: ['event.template.edit'],
  },
  {
    key: 'EditEventTemplates',
    path: `/${app.code}/events/settings/templates/edit/:id`,
    page: withMenu(EditEventTemplatePageWithProvider),
    permissions: ['event.template.view'],
  },
  {
    key: 'EventCreate',
    path: `/${app.code}/events/create`,
    page: withMenu(CreateEventPage),
    permissions: ['event.edit'],
  },
  {
    key: 'EventEdit',
    path: `/${app.code}/events/:id/edit`,
    page: withMenu(EditEventPage),
    permissions: ['event.edit'],
  },
  {
    key: 'EventView',
    path: `/${app.code}/events/:id`,
    page: withMenu(EventViewPage),
    permissions: ['event.view'],
  },
  {
    key: 'RuleList',
    path: `/${app.code}/rules`,
    page: withMenu(RuleListPage),
    permissions: ['rule.view'],
  },
  {
    key: 'CreateRule',
    path: `/${app.code}/rules/create`,
    page: withMenu(CreateRulePage),
    permissions: ['rule.edit'],
  },
  {
    key: 'EditRule',
    path: `/${app.code}/rules/edit/:id`,
    page: withMenu(EditRulePage),
    permissions: ['rule.edit'],
  },
  {
    key: 'ViewRule',
    path: `/${app.code}/rules/view/:id`,
    page: withMenu(EditRulePage),
    permissions: ['rule.view'],
  },
  {
    key: 'Decision tables list',
    path: `/${app.code}/decisionTables`,
    page: withMenu(DecisionTableListPage),
    permissions: ['decision.view'],
  },
  {
    key: 'Edit decision table',
    path: `/${app.code}/decisionTables/edit/:id`,
    page: withMenu(DecisionTableEditPage),
    permissions: ['decision.view'],
  },
  {
    key: 'Create decision table',
    path: `/${app.code}/decisionTables/create`,
    page: withMenu(DecisionTableCreatePage),
    permissions: ['decision.edit'],
  },
  {
    key: 'DashboardList',
    path: `/${app.code}/boards`,
    page: withMenu(DashboardListPage),
    permissions: ['dashboard.view'],
  },
  {
    key: 'DashboardRule',
    path: `/${app.code}/boards/view/:id`,
    page: withMenu(DashboardViewPage),
    permissions: ['dashboard.view'],
  },
  // {
  //   key: 'DashboardAdmin',
  //   path: `/${app.code}/dashboard-admin`,
  //   page: withMenu(DashboardAdmin),
  //   params: {
  //     path: '/grafana/d/dados-volume/dados-de-volume',
  //     title: 'Dashboard Admin',
  //   },
  //   permissions: ['dashteste.view'],
  // },

  //#region Webgee
  {
    key: 'WebgeeOrganizacaoLogica',
    path: `/${app.code}/configuracoes/ativos/organizacao-logica`,
    page: withMenu(WebgeePage),
    permissions: ['logichierarchy.view'],
    params: {
      relativePath: '#/configuracoes/hierarquia_unidades/visualizacao',
    }
  },
  {
    key: 'WebgeeAtribuicaoUsuario',
    path: `/${app.code}/configuracoes/ativos/atribuicao-usuario`,
    page: withMenu(WebgeePage),
    permissions: ['userassignment.view'],
    params: {
      relativePath: '#/configuracoes/usuarios/visualizacao',
    }
  },
  {
    key: 'WebgeeAutomacaoFaturas',
    path: `/${app.code}/contratos/contratos-fornecimento/aprovacao-fatura`,
    permissions: ['invoiceautomation.view'],
    page: withMenu(WebgeePage),
    params: {
      relativePath: '#/automacao_faturas/gestao_faturas/consulta_faturas_admin',
    },
  },
  {
    key: 'WebgeeResumoProcessamentoFatura',
    path: `/${app.code}/contratos/contratos-fornecimento/resumo-processamento-fatura`,
    page: withMenu(WebgeePage),
    permissions: ['invoiceautomationdashboard.view'],
    params: {
      relativePath: '#/automacao_faturas',
    },
  },
  {
    key: 'WebgeeFreemarket',
    path: `/${app.code}/contratos/outros-contratos/mercado-livre`,
    page: withMenu(WebgeePage),
    permissions: ['freemarket.view'],
    params: {
      relativePath: '#/mercado_livre/contratos/interface_acl',
    },
  },
  {
    key: 'WebgeeFreemarketMetering',
    path: `/${app.code}/contratos/outros-contratos/medicao-mercado-livre`,
    page: withMenu(WebgeePage),
    permissions: ['freemarketmetering.view'],
    params: {
      relativePath: '#/mercado_livre/medicoes/medicoes',
    },
  },
  {
    key: 'WebgeeFreemarketMigration',
    path: `/${app.code}/contratos/outros-contratos/migracoes-contratos-mercado-livre`,
    page: withMenu(WebgeePage),
    permissions: ['freemarketmigration.view'],
    params: {
      relativePath: '#/mercado_livre/migracoes/pesquisa',
    }
  },
  {
    key: 'WebgeeDistributedGeneration',
    path: `/${app.code}/contratos/outros-contratos/geracao-distribuida`,
    page: withMenu(WebgeePage),
    permissions: ['distributedgeneration.view'],
    params: {
      relativePath: '#/geracao_distribuida/contratos/pesquisa',
    },
  },
  {
    key: 'WebgeeDistributedGenerationMetering',
    path: `/${app.code}/contratos/outros-contratos/medicao-geracao-distribuida`,
    page: withMenu(WebgeePage),
    permissions: ['distributedgenerationmetering.view'],
    params: {
      relativePath: '#/geracao_distribuida/medicoes/medicoes',
    }
  },
  {
    key: 'WebgeeVersoesTarifarias',
    path: `/${app.code}/orcamento/projecoes-tarifarias/versoes-tarifarias`,
    page: withMenu(WebgeePage),
    permissions: ['tariffversion.view'],
    params: {
      relativePath: '#/projecoes_tarifarias/versionamento/pesquisa',
    },
  },
  {
    key: 'WebgeePercentualUsoUC',
    path: `/${app.code}/orcamento/projecoes-tarifarias/percentual-uso-uc`,
    page: withMenu(WebgeePage),
    permissions: ['consumptionunitpercentage.view'],
    params: {
      relativePath: '#/projecoes_tarifarias/versao_unidades/pesquisa',
    },
  },
  {
    key: 'WebgeeAnaliseProjecao',
    path: `/${app.code}/orcamento/projecoes-tarifarias/analise-projecao`,
    page: withMenu(WebgeePage),
    permissions: ['projectionanalysis.view'],
    params: {
      relativePath: '#/projecoes_tarifarias/projecao/analise_projecoes',
    }
  },
  {
    key: 'WebgeeComparativoVersoes',
    path: `/${app.code}/orcamento/projecoes-tarifarias/comparativo-versoes`,
    page: withMenu(WebgeePage),
    permissions: ['versioncomparison.view'],
    params: {
      relativePath: '#/projecoes_tarifarias/projecao/compare_projecoes',
    }
  },
  {
    key: 'WebgeeParametrizacoes',
    path: `/${app.code}/orcamento/parametrizacoes`,
    page: withMenu(WebgeePage),
    permissions: ['budgetparameterization.view'],
    params: {
      relativePath: '#/orcamento/parametrizacao/versoes',
    }
  },
  {
    key: 'WebgeeVersoesOrcamento',
    path: `/${app.code}/orcamento/versoes-orcamento`,
    page: withMenu(WebgeePage),
    permissions: ['budgetversion.view'],
    params: {
      relativePath: '#/orcamento/operacao/versoesAdmin',
    }
  },
  {
    key: 'WebgeeAnaliseDemanda',
    path: `/${app.code}/analises/analise-demanda`,
    page: withMenu(WebgeePage),
    permissions: ['demandanalysis.view'],
    params: {
      relativePath: '#/analise-demanda/faturas-lancadas',
    }
  },
  {
    key: 'WebgeeCadastroKPIs',
    path: `/${app.code}/analises/paineis-controle/kpis`,
    page: withMenu(WebgeePage),
    params: {
      relativePath: '#/datalake/indicadores/pesquisa',
    }
  },
  {
    key: 'WebgeePaineisAnaliticos',
    path: `/${app.code}/analises/paineis-controle/paineis-analiticos`,
    page: withMenu(WebgeePage),
    permissions: ['analyticaldashboards.view'],
    params: {
      relativePath: '#/datalake/visualizacao/paineis_analiticos',
    }
  },
  {
    key: 'WebgeePaineisGerenciais',
    path: `/${app.code}/analises/paineis-controle/paineis-gerenciais`,
    page: withMenu(WebgeePage),
    permissions: ['managementdashboards.view'],
    params: {
      relativePath: '#/datalake/visualizacao/paineis_gerenciais',
    }
  },
  {
    key: 'WebgeeFornecedores',
    path: `/${app.code}/configuracoes/outras-configuracoes/fornecedores`,
    page: withMenu(WebgeePage),
    permissions: ['vendor.view'],
    params: {
      relativePath: '#/fornecedores/visualizacao',
    }
  },
  {
    key: 'WebgeeDistribuidores',
    path: `/${app.code}/configuracoes/outras-configuracoes/distribuidores`,
    page: withMenu(WebgeePage),
    permissions: ['distributor.view'],
    params: {
      relativePath: '#/distribuidoras/visualizacao',
    }
  },
  {
    key: 'WebgeeEmpresasGrupo',
    path: `/${app.code}/configuracoes/empresas-do-grupo`,
    page: withMenu(WebgeePage),
    permissions: ['groupcompany.view'],
    params: {
      relativePath: '#/configuracoes/empresas_cnpj/visualizacao',
    }
  },
  {
    key: 'WebgeeMigracaoMercadoLivre',
    path: `/${app.code}/contratos/outros-contratos/migracao-mercado-livre`,
    page: withMenu(WebgeePage),
    permissions: ['freemarketmigration.view'],
    params: {
      relativePath: '#/mercado_livre/migracoes/pesquisa',
    }
  },
  //#endregion Webgee





  //#region Vero
  {
    key: 'VeroAddons',
    path: `/${app.code}/configuracoes/sistema/add-ons`,
    page: withMenu(VeroPage),
    permissions: ['addon.view'],
    params: {
      relativePath: '#vero_addons',
    },
  },
  {
    key: 'VeroParametros',
    path: `/${app.code}/configuracoes/sistema/parametros`,
    page: withMenu(VeroPage),
    permissions: ['parameter.view'],
    params: {
      relativePath: '#vero_parameters',
    },
  },
  {
    key: 'VeroTrilhaAuditoria',
    path: `/${app.code}/configuracoes/sistema/trilha-auditoria`,
    page: withMenu(AuditTrailsPage),
    permissions: ['audittrail.view'],
    params: {
      relativePath: '#',
    }
  },
  {
    key: 'VeroConfigsAuditoria',
    path: `/${app.code}/configuracoes/sistema/configuracoes-audit`,
    page: withMenu(AuditSettingsPage),
    permissions: ['audittrail.edit'],
    params: {
      relativePath: '#',
    }
  },
  {
    key: 'VeroInsumosEnergeticos',
    path: `/${app.code}/configuracoes/sistema/insumos-energeticos`,
    page: withMenu(VeroPage),
    permissions: ['energyresource.view'],
    params: {
      relativePath: '#vero_siteEnergyResources',
    }
  },
  {
    key: 'VeroTiposAtivos',
    path: `/${app.code}/configuracoes/ativos/tipos-ativos`,
    page: withMenu(VeroPage),
    permissions: ['assettype.view'],
    params: {
      relativePath: '#vero_equipmentTypes',
    }
  },
  {
    key: 'VeroHierarquiaAtivos',
    path: `/${app.code}/configuracoes/ativos/hierarquia-ativos`,
    page: withMenu(VeroPage),
    permissions: ['assethierarchy.view'],
    params: {
      relativePath: '#vero_enterpriseStructure',
    }
  },
  {
    key: 'VeroGraficoMedicao',
    path: `/${app.code}/medicao/graficos-medicao`,
    page: withMenu(VeroPage),
    permissions: ['measurementchart.view'],
    params: {
      relativePath: '#vero_measurements',
    }
  },
  {
    key: 'VeroEntradaManual',
    path: `/${app.code}/medicao/entrada-manual`,
    page: withMenu(ManualInputPage),
    permissions: ['manualinput.view']
  },
  {
    key: 'VeroRecargaAgentes',
    path: `/${app.code}/medicao/recarga-agentes`,
    page: withMenu(VeroPage),
    permissions: ['agentecharge.view'],
    params: {
      relativePath: '#vero_userBatches',
    }
  },
  {
    key: 'VeroPartesContratuais',
    path: `/${app.code}/contratos/partes-contratuais`,
    page: withMenu(VeroPage),
    permissions: ['contractpart.view'],
    params: {
      relativePath: '#vero_contractParties',
    }
  },
  {
    key: 'VeroModelosContratos',
    path: `/${app.code}/contratos/contratos-fornecimento/modelos-contratos`,
    page: withMenu(VeroPage),
    permissions: ['contracttemplate.view'],
    params: {
      relativePath: '#vero_contractTemplates',
    }
  },
  {
    key: 'VeroContratosFornecedores',
    path: `/${app.code}/contratos/contratos-fornecimento/contratos-fornecedor`,
    page: withMenu(VeroPage),
    permissions: ['vendorcontract.view'],
    params: {
      relativePath: '#vero_partyContracts',
    }
  },
  {
    key: 'VeroContratos',
    path: `/${app.code}/contratos/contratos-fornecimento/contratos`,
    page: withMenu(VeroPage),
    permissions: ['contract.view'],
    params: {
      relativePath: '#vero_equipmentContracts',
    }
  },
  {
    key: 'VeroLancamentoFatura',
    path: `/${app.code}/contratos/contratos-fornecimento/lancamento-fatura`,
    page: withMenu(VeroPage),
    permissions: ['invoiceposting.view'],
    params: {
      relativePath: '#vero_invoiceDataInput',
    }
  },
  {
    key: 'VeroConsultaFatura',
    path: `/${app.code}/contratos/contratos-fornecimento/consulta-fatura`,
    page: withMenu(VeroPage),
    permissions: ['invoicequery.view'],
    params: {
      relativePath: '#vero_invoiceQuery',
    }
  },
  {
    key: 'VeroSimulacaoFatura',
    path: `/${app.code}/contratos/contratos-fornecimento/simulacao-fatura`,
    page: withMenu(VeroPage),
    permissions: ['invoicesimulation.view'],
    params: {
      relativePath: '#vero_invoiceSimulation',
    }
  },
  {
    key: 'VeroSimulacaoContrato',
    path: `/${app.code}/contratos/contratos-fornecimento/simulacao-contrato`,
    page: withMenu(VeroPage),
    permissions: ['contractsimulation.view'],
    params: {
      relativePath: '#vero_contractsSimulation',
    }
  },
  {
    key: 'VeroAnaliseFatura',
    path: `/${app.code}/contratos/contratos-fornecimento/analise-fatura`,
    page: withMenu(VeroPage),
    permissions: ['invoiceanalysis.view'],
    params: {
      relativePath: '#vero_invoiceAnalysis',
    }
  },
  {
    key: 'VeroRelatorios1.0',
    path: `/${app.code}/analises/relatorios/relatorios-1.0`,
    page: withMenu(VeroPage),
    permissions: ['veroreport.view'],
    params: {
      relativePath: '#vero_reports',
    }
  },
  {
    key: 'VeroTabelasDinamicas',
    path: `/${app.code}/analises/relatorios/tabelas-dinamicas`,
    page: withMenu(VeroPage),
    permissions: ['dynamictable.view'],
    params: {
      relativePath: '#vero_dynamicTables',
    }
  },
  {
    key: 'VeroPermissaoUsuario',
    path: `/${app.code}/configuracoes/ativos/permissao-usuario`,
    page: withMenu(VeroPage),
    permissions: ['verousers.view'],
    params: {
      relativePath: '#vero_users',
    }
  },

  //#endregion Vero
];
export default pages;
